import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { fetchConfig } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {AbortController | undefined} */
  #recommendationsDragController;

  #suppressRecommendationsClick = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener('submit', this.#handleRecommendationSubmit);
    this.addEventListener('pointerdown', this.#handleRecommendationsPointerDown);
    this.addEventListener('click', this.#handleRecommendationsClick, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener('submit', this.#handleRecommendationSubmit);
    this.removeEventListener('pointerdown', this.#handleRecommendationsPointerDown);
    this.removeEventListener('click', this.#handleRecommendationsClick, { capture: true });
    this.#recommendationsDragController?.abort();
  }

  #handleCartUpdate = (event) => {
    const eventSource = event.detail?.data?.source;
    const didError = event.detail?.data?.didError;

    if (this.hasAttribute('auto-open') && eventSource === 'product-form-component' && !didError) {
      this.showDialog();
    }

    this.#refreshKlarnaPlacements();
  };

  #refreshKlarnaPlacements = () => {
    window.setTimeout(() => {
      const drawerPlacement = this.querySelector('.cart__drawer-klarna');
      const purchaseAmount =
        drawerPlacement?.getAttribute('data-purchase-amount') || drawerPlacement?.getAttribute('data-purchase_amount');

      if (drawerPlacement && purchaseAmount) {
        drawerPlacement.setAttribute('data-purchase-amount', purchaseAmount);
        drawerPlacement.setAttribute('data-purchase_amount', purchaseAmount);
      }

      if (purchaseAmount) {
        window.KOSMApp?.updatePurchaseAmount?.(Number(purchaseAmount));
      }

      window.Klarna?.OnsiteMessaging?.refresh?.();
      window.KlarnaOnsiteService?.push({ eventName: 'refresh-placements' });
    }, 0);
  };

  /**
   * Adds cart drawer upsell items without leaving the drawer, then lets the
   * existing cart update listener morph the refreshed section HTML.
   *
   * @param {SubmitEvent} event
   */
  #handleRecommendationSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    if (!event.target.matches('.cart-drawer__recommendation-form')) return;

    event.preventDefault();

    const form = event.target;
    const submitButton = form.querySelector('.cart-drawer__recommendation-button');

    if (submitButton instanceof HTMLButtonElement && submitButton.disabled) return;

    const formData = new FormData(form);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    if (cartItemComponentsSectionIds.length) {
      formData.set('sections', cartItemComponentsSectionIds.join(','));
    }

    if (!formData.has('sections_url')) {
      formData.set('sections_url', window.location.pathname);
    }

    const itemCount = Number(formData.get('quantity')) || 1;
    const productId = form.dataset.productId;
    const fetchCfg = fetchConfig('javascript', { body: formData });

    submitButton?.setAttribute('disabled', '');

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          this.dispatchEvent(
            new CartAddEvent({}, form.getAttribute('id') || '', {
              didError: true,
              source: 'product-form-component',
              itemCount,
              productId,
            })
          );

          return;
        }

        const variantId = formData.get('id');

        if (!variantId) throw new Error('Form ID is required');

        this.dispatchEvent(
          new CartAddEvent({}, variantId.toString(), {
            source: 'product-form-component',
            itemCount,
            productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        submitButton?.removeAttribute('disabled');
        cartPerformance.measureFromEvent('upsell-add:user-action', event);
        const cartDrawer = document.querySelector('cart-drawer-component');
        if (cartDrawer && 'open' in cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      });
  };

  /**
   * Enables mouse drag-to-scroll for the cart recommendations rail.
   * Native touch scrolling is left to the browser.
   *
   * @param {PointerEvent} event
   */
  #handleRecommendationsPointerDown = (event) => {
    if (event.button !== 0 || event.pointerType === 'touch') return;
    if (!(event.target instanceof Element)) return;

    const recommendationsList = event.target.closest('.cart-drawer__recommendations-list');
    if (!(recommendationsList instanceof HTMLElement)) return;
    if (recommendationsList.scrollWidth <= recommendationsList.clientWidth) return;

    this.#recommendationsDragController?.abort();

    const controller = new AbortController();
    const { signal } = controller;
    const startX = event.clientX;
    const startY = event.clientY;
    const startScrollLeft = recommendationsList.scrollLeft;
    let isDragging = false;

    this.#recommendationsDragController = controller;

    const stopDragging = () => {
      recommendationsList.classList.remove('is-dragging');
      controller.abort();

      if (this.#recommendationsDragController === controller) {
        this.#recommendationsDragController = undefined;
      }

      if (isDragging) {
        window.setTimeout(() => {
          this.#suppressRecommendationsClick = false;
        }, 250);
      }
    };

    /** @param {PointerEvent} event */
    const handlePointerMove = (event) => {
      const deltaX = startX - event.clientX;
      const deltaY = startY - event.clientY;

      if (!isDragging) {
        const hasDragged = Math.abs(deltaX) > 5;
        const isMostlyHorizontal = Math.abs(deltaX) > Math.abs(deltaY);

        if (!hasDragged) return;
        if (!isMostlyHorizontal) return stopDragging();

        isDragging = true;
        this.#suppressRecommendationsClick = true;
        recommendationsList.classList.add('is-dragging');
        recommendationsList.setPointerCapture(event.pointerId);
      }

      event.preventDefault();
      recommendationsList.scrollLeft = startScrollLeft + deltaX;
    };

    document.addEventListener('pointermove', handlePointerMove, { signal });
    document.addEventListener('pointerup', stopDragging, { signal });
    document.addEventListener('pointercancel', stopDragging, { signal });
    recommendationsList.addEventListener('lostpointercapture', stopDragging, { signal });
  };

  /**
   * Prevents recommendation link activation after a drag gesture.
   *
   * @param {MouseEvent} event
   */
  #handleRecommendationsClick = (event) => {
    if (!this.#suppressRecommendationsClick) return;
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.cart-drawer__recommendations-list')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#suppressRecommendationsClick = false;
  };

  open() {
    this.showDialog();
    this.#refreshKlarnaPlacements();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
